import React, { useId } from 'react';

/** Shared patient/Studio scrub renderer. Independent body, trim and optional
 * pocket piping layers. Provider branding is a separate approved asset. */
export default function LayeredScrubSvg({
  body = '#6B7280',
  trim = '#374151',
  pocketPipingHex = null,
  showBadge = true,
  provider = 'nhs',
  className = '',
  title,
}) {
  const rawId = useId().replace(/:/g, '');
  const ids = {
    clip: `uniform-clip-${rawId}`,
    bodyLight: `uniform-body-light-${rawId}`,
    trimLight: `uniform-trim-light-${rawId}`,
    neckShadow: `uniform-neck-shadow-${rawId}`,
    logo: `uniform-nhs-logo-${rawId}`,
  };
  const badgeVisible = showBadge && provider === 'nhs';
  const piping = typeof pocketPipingHex === 'string' && /^#[0-9a-fA-F]{6}$/.test(pocketPipingHex) ? pocketPipingHex : null;

  return (
    <svg viewBox="0 0 1254 1254" className={className} role={title ? 'img' : undefined} aria-label={title || undefined} aria-hidden={title ? undefined : true} data-template="scrub-vneck-v1">
      <defs>
        <clipPath id={ids.clip}>
          <path d="M470 40 C560 69 688 69 779 40 L997 140 L1196 407 C1132 467 1062 518 984 549 L955 508 L968 1162 C800 1233 455 1233 280 1162 L294 508 L264 549 C185 518 117 467 54 407 L253 140 Z" />
        </clipPath>
        <linearGradient id={ids.bodyLight} x1="0" y1="0" x2="1" y2=".15">
          <stop stopColor="#fff" stopOpacity=".09" />
          <stop offset=".45" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity=".08" />
        </linearGradient>
        <linearGradient id={ids.trimLight} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#fff" stopOpacity=".2" />
          <stop offset="1" stopColor="#000" stopOpacity=".055" />
        </linearGradient>
        <linearGradient id={ids.neckShadow} x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#000" stopOpacity=".13" />
          <stop offset="1" stopColor="#000" stopOpacity=".3" />
        </linearGradient>
        <symbol id={ids.logo} viewBox="0 0 200 80">
          <rect width="200" height="80" fill="#fff" />
          <path fill="#005eb8" d="M200 0v80H0V0h200Zm-27.5 5.5c-14.5 0-29 5-29 22 0 10.2 7.7 13.5 14.7 16.3l.7.3c5.4 2 10.1 3.9 10.1 8.4 0 6.5-8.5 7.5-14 7.5s-12.5-1.5-16-3.5L135 70c5.5 2 13.5 3.5 20 3.5 15.5 0 32-4.5 32-22.5 0-19.5-25.5-16.5-25.5-25.5 0-5.5 5.5-6.5 12.5-6.5a35 35 0 0 1 14.5 3l4-13.5c-4.5-2-12-3-20-3Zm-131 2h-22l-14 65H22l9-45h.5l13.5 45h21.5l14-65H64l-9 45h-.5l-13-45Zm63 0h-18l-13 65h17l6-28H117l-5.5 28H129l13.5-65H125L119.5 32h-20l5-24.5Z" />
        </symbol>
      </defs>
      <g id="body" data-colour-role="body">
        <path fill={body} d="M470 40 L623 333 L779 40 L997 140 L1196 407 C1132 467 1062 518 984 549 L955 508 L968 1162 C800 1233 455 1233 280 1162 L294 508 L264 549 C185 518 117 467 54 407 L253 140 Z" />
        <path fill={body} d="M500 97 Q623 119 749 97 L623 333 Z" />
        <path d="M500 97 Q623 119 749 97 L623 333 Z" fill={`url(#${ids.neckShadow})`} />
        <path d="M470 40 L623 333 L779 40 L997 140 L1196 407 C1132 467 1062 518 984 549 L955 508 L968 1162 C800 1233 455 1233 280 1162 L294 508 L264 549 C185 518 117 467 54 407 L253 140 Z" fill={`url(#${ids.bodyLight})`} />
        <g fill="#000" opacity=".035" clipPath={`url(#${ids.clip})`}>
          <path d="M253 140 Q328 335 309 392 L337 475 L337 1168 L280 1162 L294 508 C311 381 289 252 253 140 Z" />
          <path d="M997 140 Q925 335 940 392 L913 475 L913 1168 L968 1162 L955 508 C939 381 960 252 997 140 Z" />
        </g>
      </g>
      <g id="trim" data-colour-role="trim">
        <path fill={trim} d="M470 40 C560 69 688 69 779 40 L749 97 Q623 119 500 97 Z" />
        <path fill={trim} d="M470 40 L427 67 L623 370 L623 333 Z" />
        <path fill={trim} d="M779 40 L820 67 L623 370 L623 333 Z" />
        <path d="M470 40 C560 69 688 69 779 40 L749 97 Q623 119 500 97 Z" fill={`url(#${ids.trimLight})`} />
        <path d="M470 40 L427 67 L623 370 L623 333 Z" fill={`url(#${ids.trimLight})`} />
        <path d="M779 40 L820 67 L623 370 L623 333 Z" fill={`url(#${ids.trimLight})`} />
        <path fill={trim} d="M54 407 L86 366 C148 421 225 480 294 508 L264 549 C185 518 117 467 54 407 Z" />
        <path fill={trim} d="M1166 366 L1196 407 C1132 467 1062 518 984 549 L955 508 C1024 478 1102 423 1166 366 Z" />
        <path d="M54 407 L86 366 C148 421 225 480 294 508 L264 549 C185 518 117 467 54 407 Z" fill={`url(#${ids.trimLight})`} />
        <path d="M1166 366 L1196 407 C1132 467 1062 518 984 549 L955 508 C1024 478 1102 423 1166 366 Z" fill={`url(#${ids.trimLight})`} />
      </g>
      <g id="seams" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="#173F68" strokeWidth="2.5" d="M470 40 C560 69 688 69 779 40 L997 140 L1196 407 C1132 467 1062 518 984 549 L955 508 L968 1162 C800 1233 455 1233 280 1162 L294 508 L264 549 C185 518 117 467 54 407 L253 140 Z" />
        <path stroke="#164B78" strokeWidth="1.7" d="M253 140 Q329 310 309 392 C320 457 339 494 337 559 L337 1152 M997 140 Q925 310 940 392 C929 457 910 494 913 559 L913 1152" />
        <path stroke="#164B78" strokeWidth="1.7" d="M280 1136 C448 1197 805 1197 968 1136 M337 1152 L337 1114 M913 1152 L913 1114" />
        <path stroke="#164B78" strokeWidth="1.15" strokeDasharray="2 2.7" opacity=".68" d="M281 1144 C449 1205 805 1205 967 1144" />
        <path stroke="#173F68" strokeWidth="2.5" d="M54 407 L86 366 C148 421 225 480 294 508 L264 549 C185 518 117 467 54 407 Z M1166 366 L1196 407 C1132 467 1062 518 984 549 L955 508 C1024 478 1102 423 1166 366 Z" />
        <path stroke="#164B78" strokeWidth="1.7" d="M86 366 C148 421 225 480 294 508 M1166 366 C1102 423 1024 478 955 508" />
        <path stroke="#164B78" strokeWidth="1.7" d="M470 40 C560 69 688 69 779 40 M500 97 Q623 119 749 97" />
        <path stroke="#173F68" strokeWidth="2.5" d="M470 40 L427 67 L623 370 L623 333 Z M779 40 L820 67 L623 370 L623 333 Z" />
      </g>
      <g id="pockets">
        <path fill={body} d="M309 837 L542 837 L542 1084 L501 1122 L341 1116 L307 1078 Z" />
        <path fill={body} d="M705 837 L940 837 L942 1078 L908 1116 L747 1122 L705 1084 Z" />
        <path d="M309 837 L542 837 L542 1084 L501 1122 L341 1116 L307 1078 Z M705 837 L940 837 L942 1078 L908 1116 L747 1122 L705 1084 Z" fill={`url(#${ids.bodyLight})`} />
        <path fill="none" stroke="#173F68" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M309 837 L542 837 L542 1084 L501 1122 L341 1116 L307 1078 Z M705 837 L940 837 L942 1078 L908 1116 L747 1122 L705 1084 Z" />
        <path fill="none" stroke="#164B78" strokeWidth="1.7" d="M309 871 Q425 875 542 871 M705 871 Q824 875 940 871" />
      </g>
      {piping ? <g id="pocket-piping" data-colour-role="pocket-piping" fill="none" stroke={piping} strokeWidth="13" strokeLinecap="butt" strokeLinejoin="round" aria-hidden="true">
        <path d="M309 843 L542 843" />
        <path d="M705 843 L940 843" />
      </g> : null}
      {badgeVisible ? <g id="provider-logo" data-provider="nhs" data-placement="left_chest" aria-hidden="true">
        <svg x="754" y="419" width="145" height="58" viewBox="0 0 200 80" overflow="visible">
          <use href={`#${ids.logo}`} width="200" height="80" />
        </svg>
      </g> : null}
    </svg>
  );
}
